import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AnalyticValues, useAnalytics } from "../../../../contexts/analytics/hooks";
import type { OpenShiftRequest, ReplaceShiftRequest } from "../../../../utils/models";
import { useDeleteShift } from "../../../RequestData/hooks";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import SystemDialog from "../../../UIToolkit/Popups/Dialog";
import { deleteConfirmCopy } from "./gapsPostedSteps";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** The posted sheet's own event properties (department, request, type). */
	properties: AnalyticValues;
	/** Once deleted, the sheet has nothing left to show. */
	onDeleted: () => void;
	testIdPrefix: string;
};

/**
 * "Delete" on a posted shift (P3-10): `useDeleteShift`, behind the Requests screen's own delete confirm
 * (`useRequestItemActions`) — the same title, warning and action. A failed delete keeps the dialog up for a retry.
 */
export const GapsPostedDeleteRow: FC<Props> = ({ request, properties, onDeleted, testIdPrefix }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { mutateAsync: deleteShift, isPending: isDeleting } = useDeleteShift();
	const [isConfirming, setIsConfirming] = useState<boolean>(false);
	const deleteCopy = deleteConfirmCopy(request);

	const handleDelete = useCallback(async (): Promise<void> => {
		track("gaps_posted_delete_start", properties);
		try {
			await deleteShift({ shiftId: request.id });
			track("gaps_posted_delete_success", properties);
			setIsConfirming(false);
			onDeleted();
		} catch (error) {
			// `useDeleteShift` has already shown the error; the dialog stays so the user can retry or cancel.
			trackError("gaps_posted_delete_error", { ...properties, error_message: error instanceof Error ? error.message : String(error) });
		}
	}, [deleteShift, onDeleted, properties, request.id, track, trackError]);

	return (
		<>
			<SheetActionRow
				tone="danger"
				icon={<DeleteOutlineOutlinedIcon />}
				label={t("gaps.postedSheet.delete")}
				onClick={() => {
					track("gaps_posted_delete_confirm_shown", properties);
					setIsConfirming(true);
				}}
				dataTestId={`${testIdPrefix}-delete`}
			/>
			<SystemDialog
				dataTestId={`${testIdPrefix}-delete-dialog`}
				open={isConfirming}
				onClose={() => setIsConfirming(false)}
				title={t(deleteCopy.titleKey)}
				body={t(deleteCopy.bodyKey)}
				confirm={t("delete.request")}
				confirmButtonVariant="delete"
				confirmLoading={isDeleting}
				onConfirm={() => void handleDelete()}
			/>
		</>
	);
};
